import React, { useState, useEffect } from "react";
import axios from "axios";
import { Line } from "react-chartjs-2";
import { API } from "../lib/config";
import { fmt$, fmtN, fmtPct, parseOCC } from "../lib/format";

// ─── Position Detail Modal (Ultra-Comprehensive) ──────────────────────────────
export default function PositionDetailModal({ position, onClose }) {
  const [chartData, setChartData] = useState(null);
  const [intradayData, setIntradayData] = useState(null);
  const [chartMode, setChartMode] = useState("today");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveTick, setLiveTick] = useState(null);
  const [aiInsight, setAiInsight] = useState("");
  const isOption = String(position.type || "").includes("OPTION");
  const occ = isOption ? parseOCC(position.symbol) : null;
  const ticker = occ ? occ.ticker : (position.ticker || position.symbol);

  // Real-time SSE tick stream — same heartbeat/reconnect contract as
  // DiamondUI.js/SwipeCards3.js/FloorChat.js (45s heartbeat timeout, 4s
  // onerror retry). Previously had no onerror/heartbeat at all, so a
  // connection that went zombie (still "open" but silently dead — a real,
  // known SSE failure mode) would leave liveTick frozen with no recovery
  // and no way to tell it apart from a genuinely quiet ticker.
  useEffect(() => {
    if (!ticker) return;
    let cancelled = false, es, reconnect, heartbeat;
    const resetBeat = () => {
      clearTimeout(heartbeat);
      heartbeat = setTimeout(() => { if (cancelled) return; es?.close(); reconnect = setTimeout(connect, 1000); }, 45000);
    };
    const connect = () => {
      if (cancelled) return;
      clearTimeout(reconnect); es?.close();
      es = new EventSource(`${API}/stream/position?symbol=${ticker}`, { withCredentials: true });
      resetBeat();
      es.onopen = resetBeat;
      es.onerror = () => { es.close(); clearTimeout(heartbeat); reconnect = setTimeout(connect, 4000); };
      es.addEventListener("tick", e => {
        resetBeat();
        try { setLiveTick(JSON.parse(e.data)); } catch {}
      });
    };
    connect();
    return () => { cancelled = true; clearTimeout(reconnect); clearTimeout(heartbeat); es?.close(); };
  }, [ticker]);

  useEffect(() => {
    if (!ticker) return;
    setLoading(true);
    // For crypto, use ticker-USD format for OHLCV
    const chartSymbol = String(position.type || "").includes("CRYPTO") ? `${ticker}-USD` : ticker;
    Promise.allSettled([
      axios.get(`${API}/market/ohlcv?symbol=${chartSymbol}&days=30`),
      axios.get(`${API}/zi/regime?symbol=${ticker}`),
      axios.get(`${API}/market/intraday?symbol=${chartSymbol}&interval=5m&range_val=1d`),
      position.source === "autopilot" && position.strike
        ? axios.post(`${API}/trade/analysis`, {
            symbol: ticker, strike: position.strike,
            optionType: position.optionType || "call", expiry: position.expiry || "",
            side: "BUY", quantity: position.quantity || 1,
            limitPrice: position.entryPrice || position.lastPrice || 0,
            underlyingPrice: position.currentPrice || position.lastPrice || 0,
            iv: position.greeks?.iv || 0.4,
          })
        : Promise.resolve(null),
    ]).then(([ohlcvRes, regimeRes, intradayRes, analysisRes]) => {
      if (ohlcvRes.status === "fulfilled") {
        const bars = ohlcvRes.value?.data?.bars || [];
        if (bars.length > 0) {
          const labels = bars.map(b => new Date(b.t).toLocaleDateString("en-US", { month: "short", day: "numeric" }));
          const closes = bars.map(b => b.c);
          const volumes = bars.map(b => b.v);
          // Add forecast projection (simple linear)
          const last5 = closes.slice(-5);
          const trend = last5.length > 1 ? (last5[last5.length-1] - last5[0]) / last5.length : 0;
          const forecastLabels = [];
          const forecastData = [...closes.map(() => null)];
          forecastData[forecastData.length - 1] = closes[closes.length - 1]; // connect to last point
          for (let i = 1; i <= 5; i++) {
            forecastLabels.push(`+${i}d`);
            forecastData.push(closes[closes.length - 1] + trend * i);
          }
          setChartData({
            labels: [...labels, ...forecastLabels],
            closes, volumes,
            datasets: [
              { label: ticker, data: [...closes, ...Array(5).fill(null)], borderColor: "#60a5fa", backgroundColor: "rgba(96,165,250,0.06)", fill: true, pointRadius: 0, borderWidth: 1.5, tension: 0.3 },
              { label: "Forecast", data: forecastData, borderColor: "#a78bfa", borderDash: [4, 4], backgroundColor: "rgba(167,139,250,0.04)", fill: true, pointRadius: 0, borderWidth: 1.5, tension: 0.3 },
            ]
          });
        }
      }
      if (regimeRes.status === "fulfilled") {
        setAnalysis(prev => ({ ...prev, regime: regimeRes.value?.data }));
      }
      // Intraday chart
      if (intradayRes.status === "fulfilled") {
        const ibars = intradayRes.value?.data?.bars || [];
        if (ibars.length > 0) {
          const iLabels = ibars.map(b => new Date(b.t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }));
          const iCloses = ibars.map(b => b.c);
          const firstPrice = iCloses[0];
          setIntradayData({
            labels: iLabels,
            datasets: [{
              label: `${ticker} Today`, data: iCloses,
              borderColor: iCloses[iCloses.length-1] >= firstPrice ? "#4ade80" : "#f87171",
              backgroundColor: iCloses[iCloses.length-1] >= firstPrice ? "rgba(74,222,128,0.06)" : "rgba(248,113,113,0.06)",
              fill: true, pointRadius: 0, borderWidth: 1.5, tension: 0.2,
            }],
          });
        }
      }
      // Generate AI insight inline — factor in BOTH total P&L and today's momentum
      const gainPct = position.gainPct || position.pnlPct || 0;
      const dayGain = position.dayGainValue || 0;
      const dayGainPct = position.dayGainPct || 0;
      let insight = "";
      // Today's momentum takes priority for actionable insights
      if (dayGainPct > 30) insight = `Surging +${fmtN(dayGainPct,1)}% today — strong momentum. Consider scaling out a portion to lock in today's gains. ${gainPct > 0 ? "Total position is profitable — trail your stop." : "Total P&L is recovering — let the momentum work."}`;
      else if (dayGainPct > 15) insight = `Up +${fmtN(dayGainPct,1)}% today — bullish momentum building. ${gainPct > 10 ? "Position is solidly profitable — tighten trailing stop to protect gains." : gainPct > -5 ? "Approaching breakeven — today's move is working in your favor." : "Recovery underway — hold with a tight stop at today's open price."}`;
      else if (dayGainPct < -25) insight = `Sharp decline of ${fmtN(dayGainPct,1)}% today — evaluate exit. ${gainPct < -20 ? "Combined with total drawdown, consider cutting to preserve capital." : "Selling pressure is heavy — monitor for potential dead cat bounce."}`;
      else if (dayGainPct < -10) insight = `Weak session (${fmtN(dayGainPct,1)}% today). The Omega Risk Gate monitors the tier-based stop loss. If loss exceeds the Survival tier's 3% account risk, an auto-exit will trigger.`;
      else if (gainPct > 30) insight = "Strong unrealized gains. Consider scaling out 50% to lock profits while letting the rest run. The Omega exit priority suggests taking profits above 50% gain.";
      else if (gainPct > 10) insight = "Position is in profit. Hold with a trailing stop at the entry price to protect gains. Monitor DTE — if under 21 days, the 21-DTE discipline will trigger evaluation.";
      else if (gainPct > -5) insight = "Position is near breakeven. Patience is key — the Omega engine evaluates every 5 minutes. Current market regime determines if this setup remains valid.";
      else if (gainPct > -20) insight = `Total P&L is ${fmtN(gainPct,1)}%. The position is in a modest drawdown but within normal range. Monitor for reversal signals and manage via the Omega Risk Gate.`;
      else insight = "Significant unrealized loss. The Posterior Degradation Exit will fire if conviction drops >15 points from entry. Consider cutting the position to preserve capital.";
      setAiInsight(insight);
      if (analysisRes?.status === "fulfilled" && analysisRes.value?.data) {
        setAnalysis(prev => ({ ...prev, ...analysisRes.value.data }));
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticker]);

  const co = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true, labels: { color: "var(--muted)", boxWidth: 8, font: { size: 9 } } } }, scales: { x: { ticks: { color: "var(--dim)", font: { size: 8 }, maxTicksLimit: 8 }, grid: { color: "var(--grid)" } }, y: { ticks: { color: "var(--dim)", font: { size: 9 } }, grid: { color: "var(--grid)" } } } };

  return (
    <div data-testid="position-detail-modal" style={{ position: "fixed", inset: 0, zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.75)" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 16, width: "min(95vw, 860px)", maxHeight: "90vh", overflow: "auto", padding: 24, boxShadow: "0 24px 80px rgba(0,0,0,0.6)" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: "var(--text)" }}>{ticker}</span>
            {occ ? (
              <>
                <span style={{ fontSize: 14, color: "#60a5fa", fontWeight: 500 }}>${occ.strike} {occ.side}</span>
                <span className="tag t-pur" style={{ fontSize: 9 }}>Exp {occ.expDate}</span>
              </>
            ) : (
              <>
                {position.strike && <span style={{ fontSize: 14, color: "var(--muted)" }}>${position.strike} {(position.optionType || "").toUpperCase()}</span>}
                {position.expiry && <span className="tag t-pur" style={{ fontSize: 9 }}>Exp {position.expiry}</span>}
              </>
            )}
            <span className="tag t-bull" style={{ fontSize: 8, animation: "pulse 2s infinite" }}>LIVE</span>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 20, cursor: "pointer" }} data-testid="close-detail-modal">X</button>
        </div>

        {/* Everything below already null-guards its own data (chartData/
            intradayData/aiInsight/analysis?.* all render their own "Loading…"
            fallback independently) — no longer gated behind one combined
            `loading` flag. That used to hide the whole modal (including an
            already-fetched chart) for up to ~15s while the slowest of 4
            parallel requests finished, which read as a stuck/broken modal. */}
        <>
          {loading && (
            <div style={{ textAlign: "center", padding: "6px 0 14px", color: "var(--dim)", fontSize: 11 }}>
              <span className="spin" style={{ marginRight: 6 }} /> Fetching full analysis…
            </div>
          )}
          {/* Chart Toggle + Live Chart */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
              <button onClick={() => setChartMode("today")} style={{ padding: "3px 10px", borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: "pointer", border: chartMode === "today" ? "1px solid rgba(56,189,248,0.5)" : "1px solid var(--border)", background: chartMode === "today" ? "rgba(56,189,248,0.1)" : "transparent", color: chartMode === "today" ? "#38bdf8" : "var(--muted)" }} data-testid="chart-today-btn">Today</button>
              <button onClick={() => setChartMode("30d")} style={{ padding: "3px 10px", borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: "pointer", border: chartMode === "30d" ? "1px solid rgba(56,189,248,0.5)" : "1px solid var(--border)", background: chartMode === "30d" ? "rgba(56,189,248,0.1)" : "transparent", color: chartMode === "30d" ? "#38bdf8" : "var(--muted)" }} data-testid="chart-30d-btn">30 Day</button>
            </div>
            <div style={{ height: 200, background: "var(--layer2)", borderRadius: 10, padding: 10 }}>
              {chartMode === "today" && intradayData ? (
                <Line data={intradayData} options={co} />
              ) : chartMode === "today" && chartData ? (
                <div style={{ position: "relative", height: "100%" }}>
                  <Line data={chartData} options={co} />
                  <div style={{ position: "absolute", top: 8, right: 8, padding: "2px 8px", borderRadius: 4, background: "rgba(251,191,36,0.15)", color: "#fbbf24", fontSize: 9, fontWeight: 600 }}>
                    Market closed — showing recent history
                  </div>
                </div>
              ) : chartMode === "today" ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--dim)", fontSize: 12 }}>Loading chart data...</div>
              ) : chartData ? (
                <Line data={chartData} options={co} />
              ) : (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--dim)", fontSize: 12 }}>Loading chart...</div>
              )}
            </div>
          </div>

          {/* Omega AI Insight */}
          {aiInsight && (
            <div style={{ padding: 12, borderRadius: 10, background: "rgba(167,139,250,0.04)", border: "1px solid rgba(167,139,250,0.12)", marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#a78bfa", marginBottom: 4 }}>OMEGA AI INSIGHT</div>
              <div style={{ fontSize: 11, color: "var(--text)", lineHeight: 1.6 }}>{aiInsight}</div>
            </div>
          )}

          {/* Live Tick Bar */}
          {liveTick && liveTick.price > 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "8px 14px", marginBottom: 14, borderRadius: 8, background: "rgba(56,189,248,0.04)", border: "1px solid rgba(56,189,248,0.08)", fontSize: 11 }}>
              <span style={{ fontWeight: 700, color: "#38bdf8" }}>LIVE</span>
              <span style={{ color: "var(--text)", fontWeight: 600 }}>${fmtN(liveTick.price, 4)}</span>
              <span style={{ color: "var(--muted)" }}>Bid: ${fmtN(liveTick.bid, 4)}</span>
              <span style={{ color: "var(--muted)" }}>Ask: ${fmtN(liveTick.ask, 4)}</span>
              {liveTick.volume > 0 && <span style={{ color: "var(--muted)" }}>Vol: {Number(liveTick.volume).toLocaleString()}</span>}
              <span style={{ marginLeft: "auto", fontSize: 9, color: "var(--dim)" }}>{new Date(liveTick.ts).toLocaleTimeString()}</span>
            </div>
          ) : String(position.type || "").includes("CRYPTO") ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", marginBottom: 14, borderRadius: 8, background: "rgba(250,204,21,0.04)", border: "1px solid rgba(250,204,21,0.08)", fontSize: 11 }}>
              <span style={{ fontWeight: 700, color: "#facc15" }}>CRYPTO</span>
              <span style={{ color: "var(--muted)" }}>Live tick streaming not available for crypto — using last known price</span>
            </div>
          ) : null}

          {/* Key Metrics Grid — Today's P&L is primary signal */}
          {(position.dayGainValue != null || position.dayGainPct != null) && (
            <div style={{ display: "flex", gap: 12, marginBottom: 14, padding: "10px 14px", borderRadius: 10, background: (position.dayGainPct || 0) >= 0 ? "rgba(74,222,128,0.05)" : "rgba(248,113,113,0.05)", border: (position.dayGainPct || 0) >= 0 ? "1px solid rgba(74,222,128,0.15)" : "1px solid rgba(248,113,113,0.15)" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: "var(--muted)", marginBottom: 2 }}>Today's P&L</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: (position.dayGainPct || 0) >= 0 ? "var(--green)" : "var(--red)" }}>
                  {(position.dayGainValue || 0) >= 0 ? "+" : ""}{fmt$(position.dayGainValue || 0)}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 10, color: "var(--muted)", marginBottom: 2 }}>Today's Return</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: (position.dayGainPct || 0) >= 0 ? "var(--green)" : "var(--red)" }}>
                  {(position.dayGainPct || 0) >= 0 ? "+" : ""}{fmtN(position.dayGainPct || 0, 1)}%
                </div>
              </div>
            </div>
          )}
          <div className="grid4" style={{ marginBottom: 14 }}>
            <div className="m-box">
              <div className="m-lbl">Current Value</div>
              <div className="m-val">{fmt$(position.currentValue || position.positionValue || 0)}</div>
            </div>
            <div className="m-box">
              <div className="m-lbl">Total P&L</div>
              <div className={`m-val ${(position.gainPct ?? position.pnlPct ?? 0) >= 0 ? "pos" : "neg"}`}>
                {(position.gainPct ?? position.pnlPct ?? 0) >= 0 ? "+" : ""}{fmtPct(position.gainPct ?? position.pnlPct ?? 0)}
              </div>
            </div>
            <div className="m-box">
              <div className="m-lbl">Quantity</div>
              <div className="m-val">{isOption ? position.quantity + " contracts" : fmtN(position.quantity, 2)}</div>
            </div>
            <div className="m-box">
              <div className="m-lbl">Last Price</div>
              <div className="m-val">{fmt$(position.lastPrice || position.currentPrice)}</div>
            </div>
          </div>

          {/* Zi Score & Analysis (for Autopilot positions) */}
          {position.ziScore && (
            <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 200, padding: 14, borderRadius: 10, background: "rgba(56,189,248,0.04)", border: "1px solid rgba(56,189,248,0.1)" }}>
                <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6 }}>Omega Zi Score</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: position.ziScore >= 68 ? "#4ade80" : position.ziScore >= 53 ? "#38bdf8" : position.ziScore >= 33 ? "#fbbf24" : "#f87171" }}>
                  {position.ziScore}
                </div>
                <div className={`tag ${position.ziClass === "ULTRA_GEM" ? "t-bull" : position.ziClass === "GEM" ? "t-blue" : "t-neu"}`} style={{ fontSize: 9, marginTop: 4 }}>
                  {(position.ziClass || "").replace("_", " ")}
                </div>
              </div>

              {analysis?.mc && (
                <div style={{ flex: 1, minWidth: 200, padding: 14, borderRadius: 10, background: "rgba(167,139,250,0.04)", border: "1px solid rgba(167,139,250,0.1)" }}>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6 }}>Monte Carlo Forecast</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: "#a78bfa" }}>{fmtPct((analysis.mc.probProfit || 0) * 100)} PoP</div>
                  <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 4 }}>Expected: ${fmtN(analysis.mc.expectedValue, 4)}</div>
                  <div style={{ fontSize: 10, color: "var(--muted)" }}>95% CI: [{fmtN(analysis.mc.ci95?.[0], 2)}, {fmtN(analysis.mc.ci95?.[1], 2)}]</div>
                </div>
              )}

              {analysis?.bs && (
                <div style={{ flex: 1, minWidth: 200, padding: 14, borderRadius: 10, background: "rgba(74,222,128,0.04)", border: "1px solid rgba(74,222,128,0.1)" }}>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 6 }}>Black-Scholes</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: "#4ade80" }}>${fmtN(analysis.bs.price, 4)}</div>
                  <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 4 }}>Delta: {fmtN(analysis.bs.delta, 3)} | Gamma: {fmtN(analysis.bs.gamma, 4)}</div>
                  <div style={{ fontSize: 10, color: "var(--muted)" }}>Theta: {fmtN(analysis.bs.theta, 4)} | Vega: {fmtN(analysis.bs.vega, 4)}</div>
                </div>
              )}
            </div>
          )}

          {/* Trade Analysis Details */}
          {analysis?.maxLoss !== undefined && (
            <div className="grid4" style={{ marginBottom: 14 }}>
              <div className="m-box"><div className="m-lbl">Max Loss</div><div className="m-val neg">{fmt$(analysis.maxLoss)}</div></div>
              <div className="m-box"><div className="m-lbl">Max Profit</div><div className="m-val pos">{fmt$(analysis.maxProfit)}</div></div>
              <div className="m-box"><div className="m-lbl">Breakeven</div><div className="m-val">${fmtN(analysis.breakeven, 2)}</div></div>
              <div className="m-box"><div className="m-lbl">DTE</div><div className="m-val">{analysis.dte || 0}d</div></div>
            </div>
          )}

          {/* Regime Info */}
          {analysis?.regime && (
            <div style={{ padding: 12, borderRadius: 10, background: "rgba(250,204,21,0.04)", border: "1px solid rgba(250,204,21,0.1)", marginBottom: 14, fontSize: 11 }}>
              <span style={{ fontWeight: 600, color: "#facc15" }}>Regime:</span>{" "}
              <span className={`tag ${String(analysis.regime.regime || "").includes("bull") ? "t-bull" : "t-bear"}`} style={{ fontSize: 9 }}>
                {(analysis.regime.regimeLabel || analysis.regime.regime || "").toUpperCase()}
              </span>
              <span style={{ color: "var(--muted)", marginLeft: 8 }}>Confidence: {fmtPct((analysis.regime.confidence || 0) * 100)}</span>
              <span style={{ color: "var(--muted)", marginLeft: 8 }}>Vol: {fmtN(analysis.regime.currentVol, 1)}%</span>
            </div>
          )}

          {/* AI Insight */}
          {analysis?.aiInsight && (
            <div style={{ padding: 14, borderRadius: 10, background: "rgba(56,189,248,0.04)", border: "1px solid rgba(56,189,248,0.1)", fontSize: 12, color: "var(--text)", lineHeight: 1.6 }}>
              <span style={{ fontWeight: 600, color: "#38bdf8", fontSize: 11 }}>Omega AI Prediction:</span>
              <div style={{ marginTop: 6 }}>{analysis.aiInsight}</div>
            </div>
          )}

          {/* Position Details */}
          <div style={{ marginTop: 14, padding: 12, borderRadius: 10, background: "var(--layer1)", fontSize: 10, color: "var(--muted)", display: "flex", gap: 16, flexWrap: "wrap" }}>
            {position.entryPrice && <span>Entry: ${fmtN(position.entryPrice, 2)}</span>}
            {position.holdHours && <span>Hold: {position.holdHours < 24 ? `${fmtN(position.holdHours, 1)}h` : `${Math.round(position.holdHours / 24)}d`}</span>}
            {position.bid && <span>Bid: ${fmtN(position.bid, 2)}</span>}
            {position.ask && <span>Ask: ${fmtN(position.ask, 2)}</span>}
            {position.pctOfPortfolio && <span>Portfolio: {fmtPct(position.pctOfPortfolio)}</span>}
            {position.tradeStrategy && <span>Strategy: {position.tradeStrategy}</span>}
            <span>Source: {position.source === "autopilot" ? "Omega Autopilot" : "Manual / Public.com"}</span>
          </div>
        </>
      </div>
    </div>
  );
}
